import React, { useState, useEffect } from 'react';
import { servicesService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { Plus, Pencil, Trash2, Loader2, Briefcase, ShieldAlert, FileText } from 'lucide-react';
import { getApiError } from '@/lib/utils';

const initialFormData = {
  name: '',
  description: '',
  food_summary_report_enabled: false,
  beverage_summary_report_enabled: false,
  per_scope_completion_enabled: false,
  service_type_id: null,
};

export default function ServicesPage() {
  const { isAdmin } = useAuth();
  const { toast } = useToast();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      const data = await servicesService.list({ is_active: null }); // Show all including inactive
      setServices(data);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to load services',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedService(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const openEditDialog = (service) => {
    setSelectedService(service);
    setFormData({
      name: service.name || '',
      description: service.description || '',
      food_summary_report_enabled: service.food_summary_report_enabled || false,
      beverage_summary_report_enabled: service.beverage_summary_report_enabled || false,
      per_scope_completion_enabled: service.per_scope_completion_enabled || false,
      service_type_id: service.service_type_id ?? null,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (service) => {
    setSelectedService(service);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (selectedService) {
        await servicesService.update(selectedService.id, formData);
        toast({ title: 'Success', description: 'Service updated' });
      } else {
        await servicesService.create(formData);
        toast({ title: 'Success', description: 'Service created' });
      }
      setDialogOpen(false);
      loadServices();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to save service'),
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await servicesService.delete(selectedService.id);
      toast({ title: 'Success', description: 'Service deleted' });
      setDeleteDialogOpen(false);
      loadServices();
    } catch (err) {
      toast({
        title: 'Error',
        description: getApiError(err, 'Failed to delete service'),
        variant: 'destructive'
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Admin-only page
  if (!isAdmin) {
    return (
      <div className="text-center py-12" data-testid="services-no-access">
        <ShieldAlert className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
        <p className="text-muted-foreground">Only administrators can manage services.</p>
      </div>
    );
  }

  return (
    <div data-testid="services-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Briefcase className="h-6 w-6" />
            Services Offered
          </h1>
          <p className="text-muted-foreground">Manage services available for scheduling</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-service-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Service
        </Button>
      </div>

      {/* Services Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Services</CardTitle>
          <CardDescription>
            {services.length} service{services.length !== 1 ? 's' : ''} configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No services configured</p>
              <Button onClick={openCreateDialog} variant="outline" className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add your first service
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Service Type</TableHead>
                  <TableHead>Summary Reports</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((service) => {
                  const parent = service.service_type_id ? services.find(s => s.id === service.service_type_id) : null;
                  return (
                  <TableRow key={service.id} data-testid={`service-row-${service.id}`}>
                    <TableCell className="font-medium">{service.name}</TableCell>
                    <TableCell className="max-w-md truncate">
                      {service.description || <span className="text-muted-foreground">-</span>}
                    </TableCell>
                    <TableCell>
                      {parent ? (
                        <Badge variant="outline" className="text-xs border-sky-300 text-sky-700 bg-sky-50" data-testid={`service-${service.id}-type-badge`}>
                          ↳ {parent.name}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {service.food_summary_report_enabled && (
                          <Badge variant="outline" className="text-xs border-green-300 text-green-700 bg-green-50" data-testid={`service-${service.id}-food-badge`}>
                            <FileText className="h-3 w-3 mr-1" />Food
                          </Badge>
                        )}
                        {service.beverage_summary_report_enabled && (
                          <Badge variant="outline" className="text-xs border-blue-300 text-blue-700 bg-blue-50" data-testid={`service-${service.id}-bev-badge`}>
                            <FileText className="h-3 w-3 mr-1" />Beverage
                          </Badge>
                        )}
                        {service.per_scope_completion_enabled && (
                          <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50" data-testid={`service-${service.id}-per-scope-badge`}>
                            Per-Scope
                          </Badge>
                        )}
                        {!service.food_summary_report_enabled && !service.beverage_summary_report_enabled && (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={service.is_active ? 'default' : 'secondary'}>
                        {service.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(service)}
                          data-testid={`edit-service-${service.id}-btn`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(service)}
                          data-testid={`delete-service-${service.id}-btn`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {/* iter492b — Cap dialog at 85vh and let the form body scroll
            (native overflow-y-auto). Header + footer stay pinned. */}
        <DialogContent className="max-w-md max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{selectedService ? 'Edit Service' : 'Add Service'}</DialogTitle>
            <DialogDescription>
              {selectedService ? 'Update service details' : 'Create a new service type'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0 pr-2">
              <div className="space-y-2">
                <Label htmlFor="name">Service Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  placeholder="e.g., Food & Beverages Stock Audit"
                  data-testid="service-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Optional description"
                  rows={3}
                  data-testid="service-description-input"
                />
              </div>
              <div className="space-y-3 pt-2 border-t">
                <Label className="text-sm font-semibold flex items-center gap-1.5">
                  <FileText className="h-4 w-4" />
                  Summary Audit Reports
                </Label>
                <p className="text-xs text-muted-foreground -mt-1">
                  Toggle which summary audit reports auditors should complete for this service line
                </p>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <Label htmlFor="food-toggle" className="text-sm font-medium">Food Stock Summary Audit</Label>
                    <p className="text-xs text-muted-foreground">Expose Food Summary Report for auditors</p>
                  </div>
                  <Switch
                    id="food-toggle"
                    checked={formData.food_summary_report_enabled}
                    onCheckedChange={(v) => handleChange('food_summary_report_enabled', v)}
                    data-testid="service-food-toggle"
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <Label htmlFor="bev-toggle" className="text-sm font-medium">Beverage Stock Summary Audit</Label>
                    <p className="text-xs text-muted-foreground">Expose Beverage Summary Report for auditors</p>
                  </div>
                  <Switch
                    id="bev-toggle"
                    checked={formData.beverage_summary_report_enabled}
                    onCheckedChange={(v) => handleChange('beverage_summary_report_enabled', v)}
                    data-testid="service-bev-toggle"
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3 bg-amber-50/50">
                  <div className="pr-3">
                    <Label htmlFor="per-scope-toggle" className="text-sm font-medium">Per-Scope Completion</Label>
                    <p className="text-xs text-muted-foreground">
                      Splits jobs into Food &amp; Beverage halves with independent completion dates and auditors. Leave OFF for composite services (e.g. Cellar Ledger + Beverage) — they'll keep summary reports but use the standard single-status workflow.
                    </p>
                  </div>
                  <Switch
                    id="per-scope-toggle"
                    checked={formData.per_scope_completion_enabled}
                    onCheckedChange={(v) => handleChange('per_scope_completion_enabled', v)}
                    data-testid="service-per-scope-toggle"
                  />
                </div>
                {/* iter492 — Service Type alignment. Only per-scope-enabled
                    services can act as a parent Service Type — that's the
                    entire point of the alignment. */}
                <div className="rounded-lg border p-3 bg-sky-50/40">
                  <Label htmlFor="service-type-select" className="text-sm font-medium">Service Type (alignment)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Group this service under a parent Service Type. When a job's pricing lines all resolve to the same per-scope-enabled parent (e.g. Food Stock Audit + Beverage Stock Audit → F&amp;B Stock Audit), the parent's per-scope treatment kicks in automatically.
                  </p>
                  <select
                    id="service-type-select"
                    value={formData.service_type_id == null ? '' : String(formData.service_type_id)}
                    onChange={(e) => handleChange('service_type_id', e.target.value === '' ? null : parseInt(e.target.value, 10))}
                    className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="service-type-select"
                  >
                    <option value="">— Not aligned (this is a standalone service) —</option>
                    {services
                      .filter(s => s.per_scope_completion_enabled && s.id !== selectedService?.id)
                      .map(s => (
                        <option key={s.id} value={String(s.id)}>{s.name}</option>
                      ))}
                  </select>
                  {formData.service_type_id != null && (
                    <p className="text-[11px] text-sky-700 mt-1.5" data-testid="service-type-hint">
                      Jobs with pricing lines aligned to this parent Service Type will use the parent's per-scope treatment even if this service alone isn't per-scope.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter className="flex-shrink-0 pt-3 border-t bg-background">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="service-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {selectedService ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Service</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedService?.name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
